import React, { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { currentSeason } from "../../utils/seasons";
import { 
  Loader2, Plus, Pencil, Trash2, Trophy, ChevronDown, ChevronUp, RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const CupsAdmin = () => {
  const { token } = useAuth();
  const [cups, setCups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCup, setEditingCup] = useState(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [editingMatchIndex, setEditingMatchIndex] = useState(-1);
  const [expandedCup, setExpandedCup] = useState(null);
  const [newCupDialogOpen, setNewCupDialogOpen] = useState(false);
  const [newCup, setNewCup] = useState({
    competition_name: "",
    season: "",
    final_position: "",
    summary: "",
    matches: []
  });

  useEffect(() => {
    fetchCups();
  }, []);

  const fetchCups = async () => {
    try {
      const response = await axios.get(`${API}/cups`, { headers: { Authorization: `Bearer ${token}` }});
      setCups(response.data);
    } catch (error) {
      console.error("Failed to fetch cups:", error);
      toast.error("Failed to load cup competitions");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncCups = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/cups/sync`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(response.data.message || "Cup data synced from fixtures!");
      fetchCups();
    } catch (error) {
      toast.error("Failed to sync cup data");
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveCup = async () => {
    if (!editingCup) return;
    try {
      await axios.put(`${API}/cups/${editingCup.id}`, editingCup, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Cup competition updated!");
      setEditDialogOpen(false);
      setEditingCup(null);
      fetchCups();
    } catch (error) {
      toast.error("Failed to update cup competition");
    }
  };

  const handleCreateCup = async () => {
    if (!newCup.competition_name || !newCup.season) {
      toast.error("Please fill in competition name and season");
      return;
    }
    try {
      await axios.post(`${API}/cups`, newCup, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Cup competition created!");
      setNewCupDialogOpen(false);
      setNewCup({ competition_name: "", season: "", final_position: "", summary: "", matches: [] });
      fetchCups();
    } catch (error) {
      toast.error("Failed to create cup competition");
    }
  };

  const handleDeleteCup = async (cupId) => {
    if (!confirm("Are you sure you want to delete this cup competition?")) return;
    try {
      await axios.delete(`${API}/cups/${cupId}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Cup competition deleted");
      fetchCups();
    } catch (error) {
      toast.error("Failed to delete cup competition");
    }
  };

  const handleAddMatch = () => {
    if (!editingCup) return;
    setEditingMatch({ round: "", opponent: "", score: "", date: "", summary: "" });
    setEditingMatchIndex(-1);
    setMatchDialogOpen(true);
  };

  const handleEditMatch = (match, index) => {
    setEditingMatch({ ...match });
    setEditingMatchIndex(index);
    setMatchDialogOpen(true);
  };

  const handleSaveMatch = () => {
    if (!editingCup || !editingMatch) return;
    
    const updatedMatches = [...(editingCup.matches || [])];
    if (editingMatchIndex === -1) {
      updatedMatches.push(editingMatch);
    } else {
      updatedMatches[editingMatchIndex] = editingMatch;
    }
    
    setEditingCup({ ...editingCup, matches: updatedMatches });
    setMatchDialogOpen(false);
    setEditingMatch(null);
    setEditingMatchIndex(-1);
  };

  const handleDeleteMatch = (index) => {
    if (!editingCup) return;
    const updatedMatches = editingCup.matches.filter((_, i) => i !== index);
    setEditingCup({ ...editingCup, matches: updatedMatches });
  };

  const getCompetitionIcon = (name) => {
    if (name?.toLowerCase().includes('fa cup')) return '🏆';
    if (name?.toLowerCase().includes('efl') || name?.toLowerCase().includes('league cup') || name?.toLowerCase().includes('carabao')) return '🥈';
    if (name?.toLowerCase().includes('europa') || name?.toLowerCase().includes('conference') || name?.toLowerCase().includes('european')) return '🌍';
    return '🏅';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="admin-cups">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Trophy className="w-8 h-8 inline mr-2 text-[#F8D742]" />
          CUP COMPETITIONS
        </h1>
        <div className="flex items-center gap-2">
          <Button onClick={handleSyncCups} disabled={syncing} variant="outline" className="border-[#7C2C3B] text-[#7C2C3B] hover:bg-[#7C2C3B] hover:text-white">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {syncing ? "SYNCING..." : "SYNC FROM FIXTURES"}
          </Button>
          <Button onClick={() => setNewCupDialogOpen(true)} className="bg-[#7C2C3B] hover:bg-[#63232F]">
            <Plus className="w-4 h-4 mr-2" /> ADD COMPETITION
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-[#0F172A]">{cups.length}</div>
          <div className="text-xs text-slate-500">Total Competitions</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-amber-600">{cups.filter(c => c.season === currentSeason()).length}</div>
          <div className="text-xs text-slate-500">Current Season</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-green-600">{cups.filter(c => c.final_position?.toLowerCase().includes('winner')).length}</div>
          <div className="text-xs text-slate-500">Trophies Won</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-blue-600">{cups.reduce((sum, c) => sum + (c.matches?.length || 0), 0)}</div>
          <div className="text-xs text-slate-500">Total Matches</div>
        </div>
      </div>

      {/* Cups List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Competition</TableHead>
              <TableHead className="w-28">Season</TableHead>
              <TableHead>Final Position</TableHead>
              <TableHead className="w-24 text-center">Matches</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cups.map(cup => (
              <React.Fragment key={cup.id}>
                <TableRow className={cup.final_position?.toLowerCase().includes('winner') ? "bg-amber-50" : ""}>
                  <TableCell>
                    <button onClick={() => setExpandedCup(expandedCup === cup.id ? null : cup.id)}>
                      {expandedCup === cup.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{getCompetitionIcon(cup.competition_name)}</span>
                      <span className="font-medium">{cup.competition_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${cup.season === currentSeason() ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                      {cup.season}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-sm ${cup.final_position?.toLowerCase().includes('winner') ? "text-amber-600 font-bold" : ""}`}>
                      {cup.final_position}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-sm font-medium">
                      {cup.matches?.length || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        onClick={() => { setEditingCup({...cup}); setEditDialogOpen(true); }}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteCup(cup.id)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {/* Expanded Summary */}
                {expandedCup === cup.id && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-slate-50 p-4">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-slate-600 mb-1">Summary</p>
                          <p className="text-sm text-slate-700">{cup.summary || "No summary available"}</p>
                        </div>
                        {cup.matches?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-slate-600 mb-2">Match Results</p>
                            <div className="grid gap-2">
                              {cup.matches.map((match, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200">
                                  <div className="flex items-center gap-4">
                                    <span className="text-xs text-slate-500 w-24">{match.date}</span>
                                    <span className="font-medium text-sm">{match.round}</span>
                                    <span className="text-sm text-slate-600">vs {match.opponent}</span>
                                  </div>
                                  <span className={`font-bold text-sm ${
                                    match.score?.startsWith('W') || (match.score?.split('-')[0] > match.score?.split('-')[1]) ? 'text-green-600' :
                                    match.score?.startsWith('L') || (match.score?.split('-')[0] < match.score?.split('-')[1]) ? 'text-red-600' :
                                    'text-slate-600'
                                  }`}>
                                    {match.score}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {cups.length === 0 && (
          <div className="text-center py-12 text-slate-500">No cup competitions found</div>
        )}
      </div>

      {/* Edit Cup Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Cup Competition</DialogTitle>
          </DialogHeader>
          {editingCup && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Competition Name</label>
                  <Input
                    value={editingCup.competition_name}
                    onChange={(e) => setEditingCup({...editingCup, competition_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Season</label>
                  <Input
                    value={editingCup.season}
                    onChange={(e) => setEditingCup({...editingCup, season: e.target.value})}
                    placeholder={`e.g. ${currentSeason()}`}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Final Position</label>
                <Input
                  value={editingCup.final_position}
                  onChange={(e) => setEditingCup({...editingCup, final_position: e.target.value})}
                  placeholder="e.g. Winners, Semi-Finals, Quarter-Finals"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Summary</label>
                <Textarea
                  value={editingCup.summary}
                  onChange={(e) => setEditingCup({...editingCup, summary: e.target.value})}
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Matches Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium">Matches ({editingCup.matches?.length || 0})</label>
                  <Button onClick={handleAddMatch} size="sm" variant="outline">
                    <Plus className="w-3 h-3 mr-1" /> Add Match
                  </Button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {editingCup.matches?.map((match, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{match.round}</span>
                          <span className="text-slate-400">•</span>
                          <span className="text-sm text-slate-600">{match.opponent}</span>
                          <span className="text-slate-400">•</span>
                          <span className="font-bold text-sm">{match.score}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{match.date}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button onClick={() => handleEditMatch(match, idx)} variant="ghost" size="icon" className="h-7 w-7">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button onClick={() => handleDeleteMatch(idx)} variant="ghost" size="icon" className="h-7 w-7 text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!editingCup.matches || editingCup.matches.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-4">No matches added yet</p>
                  )}
                </div>
              </div>

              <Button onClick={handleSaveCup} className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
                SAVE CHANGES
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Match Edit Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMatchIndex === -1 ? "Add Match" : "Edit Match"}</DialogTitle>
          </DialogHeader>
          {editingMatch && (
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">Round</label>
                <Input
                  value={editingMatch.round}
                  onChange={(e) => setEditingMatch({...editingMatch, round: e.target.value})}
                  placeholder="e.g. Third Round, Quarter-Final"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Opponent</label>
                <Input
                  value={editingMatch.opponent}
                  onChange={(e) => setEditingMatch({...editingMatch, opponent: e.target.value})}
                  placeholder="e.g. QPR (H), Liverpool (A)"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Score</label>
                  <Input
                    value={editingMatch.score}
                    onChange={(e) => setEditingMatch({...editingMatch, score: e.target.value})}
                    placeholder="e.g. 2-1, TBC"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Date</label>
                  <Input
                    type="date"
                    value={editingMatch.date}
                    onChange={(e) => setEditingMatch({...editingMatch, date: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Match Summary</label>
                <Textarea
                  value={editingMatch.summary}
                  onChange={(e) => setEditingMatch({...editingMatch, summary: e.target.value})}
                  rows={2}
                  placeholder="Brief description of the match..."
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSaveMatch} className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
                {editingMatchIndex === -1 ? "ADD MATCH" : "UPDATE MATCH"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Cup Dialog */}
      <Dialog open={newCupDialogOpen} onOpenChange={setNewCupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Cup Competition</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium">Competition Name</label>
              <Input
                value={newCup.competition_name}
                onChange={(e) => setNewCup({...newCup, competition_name: e.target.value})}
                placeholder="e.g. FA Cup, EFL Cup"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Season</label>
              <Input
                value={newCup.season}
                onChange={(e) => setNewCup({...newCup, season: e.target.value})}
                placeholder={`e.g. ${currentSeason()}`}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Final Position</label>
              <Input
                value={newCup.final_position}
                onChange={(e) => setNewCup({...newCup, final_position: e.target.value})}
                placeholder="e.g. In Progress, Winners"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Summary</label>
              <Textarea
                value={newCup.summary}
                onChange={(e) => setNewCup({...newCup, summary: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>
            <Button onClick={handleCreateCup} className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
              CREATE COMPETITION
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CupsAdmin;
